import { useTranslation } from 'react-i18next';
import { remapFieldPath, resolveFields } from './schemaWalk';
import { getValueAtPath } from './values';
import type { FieldDescriptor, FormEnvelope } from './types';

/**
 * A submitted form, read back (M1-20).
 *
 * <b>Not the renderer with its inputs disabled.</b> A submitted submission is
 * immutable (api/CONVENTIONS.md, M1-19 — a database trigger enforces it), so
 * every control on it would be a control that can never do anything, and a page
 * full of greyed-out inputs invites the reader to try. What someone wants from a
 * completed checklist is to read it, so this renders a description list.
 *
 * It reuses `resolveFields` — the framework-agnostic half of M1-18's renderer —
 * so field ORDER and labels come from the same place the live form used, rather
 * than from the parsed object's own key order, which `jsonb` does not preserve
 * on round-trip (M1-16).
 */
export function FormSubmissionView({
  envelope,
  values,
}: {
  envelope: FormEnvelope;
  values: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const fields = resolveFields(envelope.schema, envelope.uiSchema);

  return (
    <dl className="space-y-2 text-sm">
      {fields.map((field) => (
        <FieldValue key={field.path} field={field} values={values} emptyLabel={t('forms.notAnswered')} />
      ))}
    </dl>
  );
}

function FieldValue({
  field,
  values,
  emptyLabel,
}: {
  field: FieldDescriptor;
  values: Record<string, unknown>;
  emptyLabel: string;
}) {
  const label = field.schema.title ?? field.key;

  // A section has no value of its own — it is a grouping, and its children are
  // stored at their own absolute paths.
  if (field.kind === 'section' && field.children) {
    return (
      <div className="space-y-2">
        <dt className="font-medium">{label}</dt>
        <dd className="ml-4 space-y-2 border-l border-[var(--color-border)] pl-3">
          <dl className="space-y-2">
            {field.children.map((child) => (
              <FieldValue key={child.path} field={child} values={values} emptyLabel={emptyLabel} />
            ))}
          </dl>
        </dd>
      </div>
    );
  }

  const value = getValueAtPath(values, field.path);

  // A repeating group's rows are read back through the SAME per-row descriptors
  // the live form rendered them with, re-rooted onto each row's real path
  // (`parts.0.partNumber`) — the identical `remapFieldPath` trick
  // `RepeatingGroupField` uses. Without it the only honest thing to show would
  // be raw JSON, which is not a record anyone can read.
  if (field.kind === 'repeatingGroup' && field.itemFields) {
    const rows = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        <dt className="font-medium">{label}</dt>
        <dd className="ml-4 space-y-3 border-l border-[var(--color-border)] pl-3">
          {rows.length === 0 ? (
            <span className="text-[var(--color-muted-foreground)]">{emptyLabel}</span>
          ) : (
            rows.map((_, index) => (
              <dl key={index} className="space-y-2">
                {field.itemFields!.map((itemField) => (
                  <FieldValue
                    key={itemField.path}
                    field={remapFieldPath(itemField, `${field.path}.${index}.${itemField.path}`)}
                    values={values}
                    emptyLabel={emptyLabel}
                  />
                ))}
              </dl>
            ))
          )}
        </dd>
      </div>
    );
  }

  return (
    <div className="grid gap-0.5 sm:grid-cols-[minmax(0,12rem)_1fr] sm:gap-3">
      <dt className="text-[var(--color-muted-foreground)]">{label}</dt>
      <dd className="break-words">{renderValue(field, value, emptyLabel)}</dd>
    </div>
  );
}

function renderValue(field: FieldDescriptor, value: unknown, emptyLabel: string) {
  if (value === undefined || value === null || value === '') {
    return <span className="text-[var(--color-muted-foreground)]">{emptyLabel}</span>;
  }

  // Signature and photo are both stored as a string reference — a data URL for a
  // signature, a blob name or URL for a photo (docs/forms-schema.md). Only the
  // ones that are actually renderable image sources are drawn; anything else
  // falls through to its text, rather than a broken-image icon standing in for a
  // value that is perfectly readable as text.
  if ((field.kind === 'signature' || field.kind === 'photo') && typeof value === 'string') {
    if (value.startsWith('data:image/') || value.startsWith('http')) {
      return (
        <img
          src={value}
          alt={field.schema.title ?? field.key}
          className="max-h-40 max-w-full rounded-md border border-[var(--color-border)]"
        />
      );
    }
  }

  if (typeof value === 'boolean') {
    // Written out rather than shown as a checkbox: a checkbox is a control, and
    // this is a record of an answer.
    return value ? '✓' : '✗';
  }

  if (Array.isArray(value)) {
    // A multi-select's chosen options. Repeating groups never reach here —
    // FieldValue renders those through their own row descriptors above.
    return value.map((item) => String(item)).join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}
